
import * as React from "react"

const MOBILE_BREAKPOINT = 768

// Function to detect if app is running as PWA
const isPWAInstalled = (): boolean => {
  // Check for display-mode: standalone (most reliable)
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check for iOS standalone mode
  if ('standalone' in window.navigator && (window.navigator as any).standalone) {
    return true;
  }
  
  return false;
}

// Function to detect mobile device from user agent
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  return mobileRegex.test(userAgent);
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    
    // If running as PWA on mobile device, always return true
    if (isPWAInstalled() && isMobileDevice()) {
      return true;
    }
    
    // Fallback to screen size detection
    return window.innerWidth < MOBILE_BREAKPOINT;
  })

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const checkMobile = () => {
      // Priority 1: PWA on mobile device always shows mobile version
      if (isPWAInstalled() && isMobileDevice()) {
        setIsMobile(true);
        return;
      }
      
      // Priority 2: Screen size detection
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    }
    
    // Listen for display-mode changes (PWA installation/uninstallation)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = () => checkMobile();
    
    // Add event listeners
    window.addEventListener('resize', checkMobile);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleDisplayModeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleDisplayModeChange);
    }
    
    // Force a check on component mount to ensure correct initial state
    checkMobile();
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleDisplayModeChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, [])

  return isMobile
}
